import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LOGINS } from '../../constants/logins-account-types';
import { ROLES, SUB_TYPES } from '../../constants/preset-constants';
import {
    LOGIN_TYPE_AUTH,
    LOGIN_TYPE_AUTH_WITH_GROUPS,
    LOGIN_TYPE_LDAP,
    LOGIN_TYPE_MANUAL
} from '../../constants/user-login-constants';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { ListQuery } from '../../models/ListQuery';
import { PasswordConfig } from "../../models/SecurityPolicyPasswordConfig";
import { UserLogin } from '../../models/UserLogin';
import { getEnrolmentNames } from '../../services/enrolmentService';
import { getPermissionNames } from '../../services/permissionService';
import { getSecurityPolicyNames } from '../../services/securityPolicyService';
import {isAuthLogin, isAuthWithGroupsLogin, isLdapLogin} from '../../services/utilities/loginsUtils';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { SecurityPolicyPasswordConfigHelper } from "../../utilities/securityPolicyPasswordConfigHelper";
import { validateConfirmPassword, validateSecurityPolicyPassword, validateUsername } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import { toast } from "react-toastify";
import ChooseCnModal from './chooseCnModal';
import Togglebox from "../../components/togglebox/togglebox";
import {useGlobalState} from "@/Providers.tsx";
import {LICENSE_SSO_ENTRA} from "@/constants/license-constants.ts";

interface LoginCardProps {
    currentItem: UserLogin;
    setIsLdapMode: React.Dispatch<React.SetStateAction<boolean>>;
    setIsAuthMode: React.Dispatch<React.SetStateAction<boolean>>;
    setIsAuthWithGroupsMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LoginCard: FC<LoginCardProps> = ({ currentItem, setIsLdapMode, setIsAuthMode, setIsAuthWithGroupsMode }) => {
    const { errors, values, initialValues, setFieldValue, setFieldTouched, validateField } = useFormikContext<any>();
    const site = useSelector((state: IAppState) => state.site.site.label);
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { buildInfo } = useGlobalState() ?? {};
    const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
        enforceUppercase: false,
        enforceLowercase: false,
        enforceDigits: false,
        enforceSpecialCharacters: false,
        enforcePasswordMinLength: false,
        passwordMinLength: -1,
    });
    const isLdapMode = isLdapLogin(values.loginTypeId);
    const isAuthMode = isAuthLogin(values.loginTypeId);
    const isAuthWithGroupsMode = isAuthWithGroupsLogin(values.loginTypeId);
    const isAuthLicensed = buildInfo?.licenceIds?.includes(LICENSE_SSO_ENTRA) ?? false;

    // Initialise login type if not set
    useEffect(() => {
        if (!values.loginType) {
            setFieldValue('loginType', LOGIN_TYPE_MANUAL);
        }
    }, []);

    //When global site selector is change make sure you clear field that depend on it
    useEffect(() => {
        if (!!!values.id) {
            setFieldValue("masterId", "");
        }
    }, [siteId]);

    useEffect(() => {
        fetchSecurityPolicyPasswordConfig();
    }, [values.securityPolicyId]);

    const fetchSecurityPolicyPasswordConfig = async () => {
        try {
            const securityPolicyPasswordConfig = await SecurityPolicyPasswordConfigHelper.fetchSecurityPolicyPasswordConfig(values.securityPolicyId, translate);
            if (securityPolicyPasswordConfig) {
                setPasswordConfig(securityPolicyPasswordConfig);
            }
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    };

    useFocusOnRouteChange(inputRef);

    const handleRadioChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const loginType = Number(event.target.value);
        setFieldValue('loginTypeId', loginType);
        // Clear relevant fields when switching between modes when creating a new login
        // and force validation because formik seems to be a state behind
        if (values.id === 0) {
            if (loginType === LOGIN_TYPE_LDAP) {
                setFieldValue('ulPassword', '');
                setFieldValue('confirmPassword', '');
                setFieldValue('loginUserEmail', '');
                setFieldValue('uniqueIdentifierOid', '');
                await setFieldTouched('uniqueIdentifier', true);
                validateField('uniqueIdentifier');
                await setFieldTouched('ulCommonName', true);
                validateField('ulCommonName');
                setFieldValue('profile', '')
                setFieldValue('profileId', '')
            }
            else if (loginType === LOGIN_TYPE_AUTH) {
                setFieldValue('ulPassword', '');
                setFieldValue('confirmPassword', '');
                await setFieldTouched('uniqueIdentifierOid', true);
                validateField('uniqueIdentifierOid');
                await setFieldTouched('ulUserName', true);
                validateField('ulUserName');
                setFieldValue('profile', '')
                setFieldValue('profileId', '')
            }
            else if (loginType === LOGIN_TYPE_AUTH_WITH_GROUPS) {
                setFieldValue('ulPassword', '');
                setFieldValue('confirmPassword', '');
                await setFieldTouched('uniqueIdentifierOid', true);
                validateField('uniqueIdentifierOid');
                await setFieldTouched('ulUserName', true);
                validateField('ulUserName');
                // For saving purposes.
                setFieldValue('profile', translate.byKey(TranslationKeys.siteGuard))
                setFieldValue('profileId', ROLES.ID_GUARD)
            }
            else {
                setFieldValue('ulCommonName', '');
                setFieldValue('profile', '')
                setFieldValue('profileId', '')
                setFieldValue('loginUserEmail', '');
                setFieldValue('uniqueIdentifier', '');
                setFieldValue('uniqueIdentifierOid', '');
                await setFieldTouched('name', true);
                validateField('name');
                await setFieldTouched('securityPolicyId', true);
                validateField('securityPolicyId');
            }
        }
        else {
            if (loginType === LOGIN_TYPE_LDAP) {
                setIsLdapMode(true);
                if (values.ulCommonName) {
                    setFieldValue('ulCommonName', values.ulCommonName);
                } else {
                    await setFieldTouched('ulCommonName', true);
                    validateField('ulCommonName');
                }
            }
            if (loginType === LOGIN_TYPE_AUTH || loginType === LOGIN_TYPE_AUTH_WITH_GROUPS) {
                setIsAuthMode(loginType === LOGIN_TYPE_AUTH);
                setIsAuthWithGroupsMode(loginType === LOGIN_TYPE_AUTH_WITH_GROUPS);
                if (values.ulCommonName) {
                    setFieldValue('ulCommonName', values.ulCommonName);
                } else {
                    await setFieldTouched('ulCommonName', true);
                    validateField('ulCommonName');
                }
                if (values.userLoginEmail) {
                    setFieldValue('userLoginEmail', values.userLoginEmail);
                }
                else {
                    await setFieldTouched('userLoginEmail', true);
                    validateField('userLoginEmail');
                }
            }
            else {
                setIsLdapMode(false);
                setIsAuthMode(false);
                setIsAuthWithGroupsMode(false)
            }
        }
    };

    const SiteField = ({ errors, translate, TranslationKeys, values }) => {
        return <>
            <div className="flex flex-row justify-between" id='siteLabelId'>
                <Label htmlFor="siteId" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.site)}:</Label>
                {errors.siteId ? <span className='text-red-600 text-xs'>{errors.siteId}</span> : null}
            </div>
            <Field
                value={values.id ? values.site : site}
                disabled={true}
                className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
        </>
    };

    const onEnabledMfa = () => {
        const newMfaEnabledState = !values.mfaEnabled;
        setFieldValue("mfaEnabled", newMfaEnabledState);
    }

    return <>
        <div className="w-full p-2 inline-flex justify-start items-center bg-white">
            {values.id
                ? <div className="ml-8 mr-5 card-details">
                    <EntityHierarchy title={values.name} tree={{ label: values.site, node: { label: values.name } }} />
                </div>
                : null}

            <div className="flex flex-col w-full py-8">
                {/* Radio buttons for Manual/LDAP selection */}
                {values.id != LOGINS.ID_SYSDBA && (
                    <div className="px-8 mb-2 w-full">
                        <div className="flex flex-row space-x-6">
                            <div className="flex items-center">
                                <Field type="radio"
                                    name="loginType"
                                    id="manual"
                                    value={LOGIN_TYPE_MANUAL}
                                    onChange={handleRadioChange}
                                    checked={!isLdapLogin(values.loginTypeId) && !isAuthLogin(values.loginTypeId)} />
                                <span className="ml-2 text-sm">{translate.byKey(TranslationKeys.manual)}</span>
                            </div>
                            <div className="flex items-center">
                                <Field type="radio"
                                    name="loginType"
                                    id="ldap"
                                    value={LOGIN_TYPE_LDAP}
                                    onChange={handleRadioChange}
                                    checked={isLdapLogin(values.loginTypeId)} />
                                <span className="ml-2 text-sm">{translate.byKey(TranslationKeys.ldap)}</span>
                            </div>
                            {isAuthLicensed && (<>
                              <div className="flex items-center">
                                <Field type="radio"
                                       name="loginType"
                                       id="auth"
                                       value={LOGIN_TYPE_AUTH}
                                       onChange={handleRadioChange}
                                       checked={isAuthLogin(values.loginTypeId)} />
                                <span className="ml-2 text-sm">{translate.byKey(TranslationKeys.identityProvider)}</span>
                              </div>
                              <div className="flex items-center">
                                <Field type="radio"
                                       name="loginType"
                                       id="authWithLoginGroups"
                                       value={LOGIN_TYPE_AUTH_WITH_GROUPS}
                                       onChange={handleRadioChange}
                                       checked={isAuthWithGroupsLogin(values.loginTypeId)} />
                                <span className="ml-2 text-sm">{translate.byKey(TranslationKeys.identityProviderWithGroups)}</span>
                              </div>
                            </>)}
                        </div>
                    </div>
                )}

                <div className="flex flex-row">
                    {/* Column 1 */}
                    <div className="flex flex-col px-8">
                        {/* Manual-specific field: Username Name */}
                        {!isLdapMode && !isAuthMode && !isAuthWithGroupsMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='userNameLabelId'>
                                    <Label
                                        htmlFor="name"
                                        className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.username)}:
                                    </Label>
                                    {errors.name && (<span className='text-red-600 text-xs'>{errors.name}</span>)}
                                </div>
                                <div className="flex flex-row space-x-2">
                                    <Field
                                        id='userNameInputFieldId'
                                        autoFocus
                                        type="text"
                                        name="name"
                                        innerRef={inputRef}
                                        className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                                        validate={validateUsername} />
                                </div>
                            </div>
                        )}

                        {/* LDAP-specific field: Common Name */}
                        {isLdapMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='userNameLabelId'>
                                    <Label
                                        htmlFor="ulCommonName"
                                        className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.commonName)}:
                                    </Label>
                                    {errors.ulCommonName && (<span className='text-red-600 text-xs'>{errors.ulCommonName}</span>)}
                                </div>
                                <div className="flex flex-row space-x-2">
                                    <Field
                                        id='ulCommonNameId'
                                        autoFocus
                                        type="text"
                                        name="ulCommonName"
                                        innerRef={inputRef}
                                        disabled
                                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input w-full cursor-not-allowed"
                                        validate={validateUsername} />
                                    <button type="button" className="px-4 py-1 w-fit bg-sidemenu-focus rounded-md shadow text-white text-sm" onClick={() => setIsOpen(true)}>
                                        {translate.byKey(TranslationKeys.choose)}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Entra-specific field: Username Email */}
                        {(isAuthMode || isAuthWithGroupsMode) && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='userLoginEmailLabelId'>
                                    <Label
                                        htmlFor="userLoginEmail"
                                        className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.email)}:
                                    </Label>
                                    {errors.email && (<span className='text-red-600 text-xs'>{errors.email}</span>)}
                                </div>
                                <div className="flex flex-row space-x-2">
                                    <Field
                                        id='userLoginEmailId'
                                        autoFocus
                                        type="text"
                                        name="userLoginEmail"
                                        innerRef={inputRef}
                                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input w-full"
                                        validate={validateUsername} />
                                </div>
                            </div>
                        )}

                        {/* LDAP-specific field: Unique Identifier */}
                        {isLdapMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='uniqueIdentifierLabelId'>
                                    <Label
                                        htmlFor="uniqueIdentifier"
                                        className="text-xs text-gray-600 mt-1" required>
                                        {translate.byKey(TranslationKeys.uniqueIdentifierLdap)}:
                                    </Label>
                                    {errors.uniqueIdentifier ? <span className='text-red-600 text-xs'>{errors.uniqueIdentifier}</span> : null}
                                </div>
                                <Field
                                    id='uniqueIdentifierInputFieldId'
                                    name="uniqueIdentifier"
                                    type="text"
                                    className="flex flex-row items-center p-1 text-gray-500 cursor-not-allowed text-sm w-full bg-gray-input"
                                    disabled
                                    validate={validateUsername} />
                            </div>
                        )}

                        {/* Permission field */}
                        {!isAuthWithGroupsMode && (
                            <div className="mb-2" style={{ width: "17rem" }}>
                                <div className="flex flex-row justify-between" id='permissionId'>
                                    <Label htmlFor='permissionId' className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.permission)}:</Label>
                                    {errors.profileId ? <span className='text-red-600 text-xs'>{errors.profileId}</span> : null}
                                </div>
                                <div id='deviceActionDropDownId'>
                                    <PaginatedSearchableSelectField
                                        name={translate.byKey(TranslationKeys.permission)}
                                        key="permissionSelectField"
                                        accessor='profileId'
                                        dataMethod={(listParameters) => getPermissionNames(listParameters, parseInt(initialValues.profileId), parseInt(user.profileId))}
                                        searchAccessor={'profileNameOriginal'}
                                        required={true}
                                        onSelect={(_, item) => {
                                            setFieldValue('profile', item?.label)
                                            setFieldValue('profileId', item?.value)
                                        }}
                                        disabled={initialValues.id == LOGINS.ID_SYSDBA || (initialValues.profileId == ROLES.ID_SYSTEM_OWNER && user.profileId != ROLES.ID_SYSTEM_OWNER) || isAuthWithGroupsMode ? true : false}
                                        selectedItemLabel={values.profile} />
                                </div>
                            </div>
                        )}

                        {/* Entra-specific field: Username Name */}
                        {(isAuthMode || isAuthWithGroupsMode) && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='userNameLabelId'>
                                    <Label
                                        htmlFor="ulUserName"
                                        className="text-xs text-gray-600 mt-1">
                                        {translate.byKey(TranslationKeys.username)}:
                                    </Label>
                                    {errors.username && (<span className='text-red-600 text-xs'>{errors.username}</span>)}
                                </div>
                                <div className="flex flex-row space-x-2">
                                    <Field
                                        id='ulUserNameId'
                                        autoFocus
                                        type="text"
                                        name="ulUserName"
                                        disabled
                                        innerRef={inputRef}
                                        className="flex flex-row items-center p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed"/>
                                </div>
                            </div>
                        )}

                        {/* Auth-Group-specific field: Unique Identifier */}
                        {isAuthWithGroupsMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='uniqueIdentifierOidLabelId'>
                                    <Label
                                        htmlFor="uniqueIdentifierOid"
                                        className="text-xs text-gray-600 mt-1">
                                        {translate.byKey(TranslationKeys.uniqueIdentifierAuth)}:
                                    </Label>
                                    {errors.uniqueIdentifier ? <span className='text-red-600 text-xs'>{errors.uniqueIdentifier}</span> : null}
                                </div>
                                <Field
                                    id='uniqueIdentifierOidInputFieldId'
                                    name="uniqueIdentifierOid"
                                    type="text"
                                    disabled
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 cursor-not-allowed text-sm w-full bg-gray-input"/>
                            </div>
                        )}

                        {/* Manual-specific fields: Password */}
                        {!isLdapMode && !isAuthMode && !isAuthWithGroupsMode && (values.id === 0 || !currentItem.isPasswordCaptured) && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='passwordLabelId'>
                                    <Label htmlFor="ulPassword" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.password)}:</Label>
                                    {errors.ulPassword ? <span className='text-red-600 text-xs' data-cy="passwordErrorMessage">{errors.ulPassword}</span> : null}
                                </div>
                                <Field id='passwordInputFieldId'
                                    name="ulPassword"
                                    type={'password'}
                                    className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                                    validate={(value) => validateSecurityPolicyPassword(value, passwordConfig, translate, true)} />
                            </div>
                        )}

                        {/* Manual-specific field: Site */}
                        {!isLdapMode && !isAuthMode && !isAuthWithGroupsMode && (
                            <div className="mb-2" style={{ width: "17rem" }}>
                                <SiteField errors={errors} translate={translate} TranslationKeys={TranslationKeys} values={values} />
                            </div>
                        )}
                    </div>

                    {/* Column 2 */}
                    <div className="flex flex-col px-8">
                        {/* Linked To field */}
                        {initialValues.id != LOGINS.ID_SYSDBA ?
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='linkedToId'>
                                    <Label htmlFor='linkedToId'
                                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.linkedTo)}:</Label>
                                    {errors.masterId ? <span className='text-red-600 text-xs'>{errors.masterId}</span> : null}
                                </div>
                                <div id='linkedDropDownId' >
                                    <PaginatedSearchableSelectField
                                        name={translate.byKey(TranslationKeys.linkedTo)}
                                        key="linkedToSelectField"
                                        accessor='masterId'
                                        dataMethod={(listParameters?: ListQuery) => getEnrolmentNames({ ...listParameters, filters: appendFilter(values.siteId ? values.siteId : siteId, 'Site', 'site.id', listParameters?.filters ?? []) })}
                                        searchAccessor={'mstDisplayname'}
                                        dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: values.id ? values.siteId : siteId, childAcceccor: "masterId" }}
                                        required={true}
                                        onSelect={(event, item) => {
                                            setFieldValue('displayName', item?.label);
                                            setFieldValue('masterId', item?.value);
                                        }}
                                        selectedItemLabel={values.displayName} />
                                </div>
                            </div> :
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='linkedToId'>
                                    <Label
                                        htmlFor="linkedToId"
                                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.linkedTo)}:</Label>
                                    {errors.linkedToId ? <span className='text-red-600 text-xs'>{errors.linkedToId}</span> : null}
                                </div>
                                <Field
                                    value={values.displayName}
                                    disabled={true}
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                            </div>}

                        {/* LDAP/Entra-specific field: Site */}
                        {(isLdapMode || (isAuthMode || isAuthWithGroupsMode)) && (
                            <div className="mb-2" style={{ width: "17rem" }}>
                                <SiteField errors={errors} translate={translate} TranslationKeys={TranslationKeys} values={values} />
                            </div>
                        )}

                        {/* Manual-specific field: Security Policy field */}
                        {!isLdapMode && !isAuthMode && !isAuthWithGroupsMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='securityId'>
                                    <Label htmlFor='securityId' className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.securityPolicy)}:</Label>
                                    {errors.securityPolicyId ? <span className='text-red-600 text-xs'>{errors.securityPolicyId}</span> : null}
                                </div>
                                <div id='SecurityPolicyDropDownId'>
                                    <PaginatedSearchableSelectField
                                        name={translate.byKey(TranslationKeys.securityPolicy)}
                                        key="securityPolicySelectField"
                                        accessor='securityPolicyId'
                                        dataMethod={(listParameters?: ListQuery) => getSecurityPolicyNames({
                                            ...listParameters,
                                            // Apply site filters and filter by User Security Policies only
                                            filters: appendFilter(values.siteId ? values.siteId : siteId, 'Site', 'site.id',
                                                appendFilter(SUB_TYPES.POLICY_USER, "Policy Type", "profileSubType.id",
                                                    listParameters?.filters ?? []
                                                ))
                                        })}
                                        searchAccessor={'securityPolicyNameOriginal'}
                                        dependent={{ accessor: "siteId", key: "site.id", name: "Site", value: values.id ? values.siteId : siteId, childAcceccor: "securityPolicyId" }}
                                        required={true}
                                        onSelect={(_, item) => {
                                            setFieldValue('securityPolicy', item?.label)
                                            setFieldValue('securityPolicyId', item?.value)
                                        }}
                                        selectedItemLabel={values.securityPolicy}
                                        disabled={initialValues.id == LOGINS.ID_SYSDBA || (initialValues.profileId == ROLES.ID_SYSTEM_OWNER && user.profileId != ROLES.ID_SYSTEM_OWNER) ? true : false} />
                                </div>
                            </div>
                        )}

                        {/* Manual-specific field: Confirm Password */}
                        {!isLdapMode && !isAuthMode && !isAuthWithGroupsMode && (values.id === 0 || !currentItem.isPasswordCaptured) && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='confirmPasswordLabelId'>
                                    <Label htmlFor="confirmPassword" className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.confirmPassword)}:</Label>
                                    {errors.confirmPassword ? <span className='text-red-600 text-xs'>{errors.confirmPassword}</span> : null}
                                </div>
                                <Field id='confirmPasswordInputFieldId'
                                    name="confirmPassword"
                                    type="password"
                                    className="flex flex-row items-center p-1 text-gray-800 text-sm w-full bg-gray-input"
                                    validate={value => validateConfirmPassword(values.ulPassword, value, translate)} />
                            </div>
                        )}

                        {/* Auth-specific field: Unique Identifier */}
                        {isAuthMode && (
                            <div className="mb-2">
                                <div className="flex flex-row justify-between" id='uniqueIdentifierOidLabelId'>
                                    <Label
                                        htmlFor="uniqueIdentifierOid"
                                        className="text-xs text-gray-600 mt-1">
                                        {translate.byKey(TranslationKeys.uniqueIdentifierAuth)}:
                                    </Label>
                                    {errors.uniqueIdentifier ? <span className='text-red-600 text-xs'>{errors.uniqueIdentifier}</span> : null}
                                </div>
                                <Field
                                    id='uniqueIdentifierOidInputFieldId'
                                    name="uniqueIdentifierOid"
                                    type="text"
                                    disabled
                                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 cursor-not-allowed text-sm w-full bg-gray-input"/>
                            </div>
                        )}

                        {/* Non-Entra Multi-factor (MFA) */}
                        {!(isAuthMode || isAuthWithGroupsMode) &&(
                          <div className="mb-2">
                            <div className="flex flex-row justify-between mb-1" id="mfaEnabled" >
                                <Label htmlFor="mfaEnabled" className="text-xs text-gray-600 mt-1">
                                    {translate.byKey(TranslationKeys.enableMfa)}:
                                </Label>
                            </div>
                            <div className="flex flex-row justify-between">
                                <Togglebox name="mfaEnabled" onChanged={onEnabledMfa} label={""} />
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
        <ChooseCnModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
}
